import { useEffect, useRef } from "react";
import * as THREE from "three";
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js";
import { ImprovedNoise } from "three/examples/jsm/math/ImprovedNoise.js";
import { makeLetterTexture } from "../lib/letterTexture";
import { getTitleTileColor } from "../lib/tileStyle";

// English Bananagrams distribution (144 tiles). Sampling from this string
// keeps the terrain deterministic while preserving the real letter weights.
const BANANAGRAMS_LETTERS =
  "AAAAAAAAAAAAA" +
  "BBB" +
  "CCC" +
  "DDDDDD" +
  "EEEEEEEEEEEEEEEEEE" +
  "FFF" +
  "GGGG" +
  "HHH" +
  "IIIIIIIIIIII" +
  "JJ" +
  "KK" +
  "LLLLL" +
  "MMM" +
  "NNNNNNNN" +
  "OOOOOOOOOOO" +
  "PPP" +
  "QQ" +
  "RRRRRRRRR" +
  "SSSSSS" +
  "TTTTTTTTT" +
  "UUUUUU" +
  "VVV" +
  "WWW" +
  "XX" +
  "YYY" +
  "ZZ";

const INITIAL_CAMERA_POSITION = new THREE.Vector3(23.859, 5.077, -0.184);
const INITIAL_CAMERA_ROTATION = new THREE.Euler(-0.178, 1.155, 0.163, "XYZ");
const MOUNTAIN_FOCUS_DISTANCE = 39;

type TileInstance = {
  matrix: THREE.Matrix4;
  letterMatrix: THREE.Matrix4;
  letter: string;
  color: THREE.Color;
};

function seededRandom(seed: number) {
  let state = seed >>> 0;
  return () => {
    state += 0x6d2b79f5;
    let value = state;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
}

export function LandscapeBackground() {
  const hostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const scene = new THREE.Scene();
    scene.fog = new THREE.Fog(0xf5f4ef, 15, 34);

    const camera = new THREE.PerspectiveCamera(34, 1, 0.1, 60);
    camera.position.copy(INITIAL_CAMERA_POSITION);
    camera.rotation.copy(INITIAL_CAMERA_ROTATION);
    // Keep the chosen framing exact while using a point at the far mountain's
    // depth as the stable orbit anchor.
    const mountainFocus = new THREE.Vector3(0, 0, -1)
      .applyQuaternion(camera.quaternion)
      .multiplyScalar(MOUNTAIN_FOCUS_DISTANCE)
      .add(camera.position);
    const baseOrbit = new THREE.Spherical().setFromVector3(
      camera.position.clone().sub(mountainFocus),
    );

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      powerPreference: "high-performance",
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.75));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.03;
    renderer.setClearColor(0x000000, 0);
    renderer.domElement.setAttribute("aria-hidden", "true");
    host.appendChild(renderer.domElement);

    scene.add(new THREE.HemisphereLight(0xfffdf7, 0xb5aa96, 2.15));

    const keyLight = new THREE.DirectionalLight(0xfffbef, 3.25);
    keyLight.position.set(-8, 14, 10);
    scene.add(keyLight);

    const fillLight = new THREE.DirectionalLight(0xdde5ed, 0.72);
    fillLight.position.set(10, 5, -8);
    scene.add(fillLight);

    const random = seededRandom(0xa21a4d);
    const noise = new ImprovedNoise();
    const instances: TileInstance[] = [];
    const matrix = new THREE.Matrix4();
    const letterMatrix = new THREE.Matrix4();
    const position = new THREE.Vector3();
    const scale = new THREE.Vector3(1, 1, 1);
    const tileQuaternion = new THREE.Quaternion();
    const flatQuaternion = new THREE.Quaternion().setFromEuler(
      new THREE.Euler(-Math.PI / 2, 0, 0),
    );
    const letterQuaternion = new THREE.Quaternion();
    const yAxis = new THREE.Vector3(0, 1, 0);
    const tilePitch = 0.205;
    const tileHeight = 0.184;
    const columns = 32;
    const rows = 35;
    const spacing = 0.79;

    for (let row = 0; row < rows; row += 1) {
      const z = 7.4 - row * spacing;

      for (let column = 0; column < columns; column += 1) {
        const x = (column - (columns - 1) / 2) * spacing;
        const broad = noise.noise(x * 0.07 + 2.3, z * 0.07 - 1.7, 0.8);
        const rolling = noise.noise(x * 0.13 - 0.8, z * 0.13 + 1.1, 7.6);
        const detail = noise.noise(x * 0.24 - 4.1, z * 0.24 + 2.6, 4.2);
        const edgeSoftening = THREE.MathUtils.smoothstep(
          1 - Math.abs(x) / (columns * spacing * 0.55),
          0,
          1,
        );
        const sampledHeight =
          0.5 + broad * 0.72 + rolling * 0.2 + detail * 0.08;
        const heightField = THREE.MathUtils.smootherstep(
          sampledHeight,
          0.08,
          0.92,
        );
        const layerCount = Math.max(
          2,
          Math.round(2 + heightField * 16 * (0.8 + edgeSoftening * 0.2)),
        );

        for (let layer = 0; layer < layerCount; layer += 1) {
          const isSurface = layer === layerCount - 1;
          const yaw = (random() - 0.5) * (isSurface ? 0.045 : 0.075);
          const jitter = isSurface ? 0.009 : 0.016;
          position.set(
            x + (random() - 0.5) * jitter,
            -2.25 + layer * tilePitch,
            z + (random() - 0.5) * jitter,
          );
          tileQuaternion.setFromAxisAngle(yAxis, yaw);
          matrix.compose(position, tileQuaternion, scale);

          const letterPosition = position.clone();
          letterPosition.y += tileHeight / 2 + 0.0025;
          letterQuaternion.copy(tileQuaternion).multiply(flatQuaternion);
          letterMatrix.compose(letterPosition, letterQuaternion, scale);

          const letter = BANANAGRAMS_LETTERS[
            Math.floor(random() * BANANAGRAMS_LETTERS.length)
          ]!;
          const color = new THREE.Color(getTitleTileColor(letter));

          instances.push({
            matrix: matrix.clone(),
            letterMatrix: letterMatrix.clone(),
            letter,
            color,
          });
        }
      }
    }

    const tileGeometry = new RoundedBoxGeometry(0.73, tileHeight, 0.73, 3, 0.055);
    const tileMaterial = new THREE.MeshPhysicalMaterial({
      color: 0xffffff,
      roughness: 0.58,
      metalness: 0,
      clearcoat: 0.08,
      clearcoatRoughness: 0.8,
    });
    const tiles = new THREE.InstancedMesh(tileGeometry, tileMaterial, instances.length);
    tiles.frustumCulled = false;
    instances.forEach((instance, index) => {
      tiles.setMatrixAt(index, instance.matrix);
      tiles.setColorAt(index, instance.color);
    });
    tiles.instanceMatrix.needsUpdate = true;
    if (tiles.instanceColor) tiles.instanceColor.needsUpdate = true;
    scene.add(tiles);

    const letterGeometry = new THREE.PlaneGeometry(0.61, 0.61);
    const letterMeshes: THREE.InstancedMesh[] = [];
    const letterTextures: THREE.Texture[] = [];
    const byLetter = new Map<string, THREE.Matrix4[]>();

    instances.forEach((instance) => {
      const matrices = byLetter.get(instance.letter) ?? [];
      matrices.push(instance.letterMatrix);
      byLetter.set(instance.letter, matrices);
    });

    for (const [letter, matrices] of byLetter) {
      const texture = makeLetterTexture(letter, renderer, 192);
      if (!texture) continue;
      letterTextures.push(texture);

      const material = new THREE.MeshBasicMaterial({
        map: texture,
        transparent: true,
        alphaTest: 0.08,
        depthWrite: true,
        toneMapped: false,
        fog: true,
      });
      const mesh = new THREE.InstancedMesh(letterGeometry, material, matrices.length);
      mesh.frustumCulled = false;
      matrices.forEach((glyphMatrix, index) => mesh.setMatrixAt(index, glyphMatrix));
      mesh.instanceMatrix.needsUpdate = true;
      letterMeshes.push(mesh);
      scene.add(mesh);
    }

    const pointer = new THREE.Vector2();
    const smoothedPointer = new THREE.Vector2();
    const neutralPointer = new THREE.Vector2();
    const cameraOrbit = new THREE.Spherical();
    const orbitPosition = new THREE.Vector3();
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const finePointer = window.matchMedia("(pointer: fine)");
    const horizontalOrbit = THREE.MathUtils.degToRad(0.35);
    const verticalOrbit = THREE.MathUtils.degToRad(0.24);
    let pointerIsActive = false;
    let needsRender = true;

    const updatePointer = (event: PointerEvent) => {
      pointer.set(
        (event.clientX / window.innerWidth - 0.5) * 2,
        (event.clientY / window.innerHeight - 0.5) * 2,
      );
      pointerIsActive =
        !reducedMotion.matches && finePointer.matches && event.pointerType !== "touch";
      needsRender = true;
    };

    const resetPointer = () => {
      pointerIsActive = false;
      needsRender = true;
    };

    const resize = () => {
      const { width, height } = host.getBoundingClientRect();
      if (!width || !height) return;

      renderer.setSize(width, height, false);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.render(scene, camera);
    };

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(host);
    resize();

    window.addEventListener("pointermove", updatePointer, { passive: true });
    document.documentElement.addEventListener("pointerleave", resetPointer);
    window.addEventListener("blur", resetPointer);
    reducedMotion.addEventListener("change", resetPointer);
    finePointer.addEventListener("change", resetPointer);

    let frame = 0;
    let previousFrame = performance.now();
    const animate = (now: number) => {
      frame = window.requestAnimationFrame(animate);
      const delta = Math.min((now - previousFrame) / 1000, 0.05);
      previousFrame = now;
      const pointerTarget = pointerIsActive ? pointer : neutralPointer;
      const distanceRemaining = smoothedPointer.distanceToSquared(pointerTarget);
      if (distanceRemaining > 0.0000001) {
        smoothedPointer.lerp(pointerTarget, 1 - Math.exp(-6 * delta));
        needsRender = true;
      } else {
        smoothedPointer.copy(pointerTarget);
      }

      if (needsRender) {
        cameraOrbit.set(
          baseOrbit.radius,
          baseOrbit.phi + smoothedPointer.y * verticalOrbit,
          baseOrbit.theta - smoothedPointer.x * horizontalOrbit,
        );
        orbitPosition.setFromSpherical(cameraOrbit).add(mountainFocus);
        camera.position.copy(orbitPosition);
        camera.lookAt(mountainFocus);
        renderer.render(scene, camera);
        needsRender = false;
      }
    };
    frame = window.requestAnimationFrame(animate);

    return () => {
      window.cancelAnimationFrame(frame);
      resizeObserver.disconnect();
      window.removeEventListener("pointermove", updatePointer);
      document.documentElement.removeEventListener("pointerleave", resetPointer);
      window.removeEventListener("blur", resetPointer);
      reducedMotion.removeEventListener("change", resetPointer);
      finePointer.removeEventListener("change", resetPointer);
      tileGeometry.dispose();
      tileMaterial.dispose();
      letterGeometry.dispose();
      letterMeshes.forEach((mesh) => {
        const material = mesh.material;
        if (Array.isArray(material)) material.forEach((item) => item.dispose());
        else material.dispose();
      });
      letterTextures.forEach((texture) => texture.dispose());
      renderer.dispose();
      renderer.domElement.remove();
    };
  }, []);

  return <div className="landscape-stage" ref={hostRef} aria-hidden="true" />;
}
